import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Panel, SectionTitle, TactLoader } from "../components/Tactical";
import { toast } from "sonner";
import { BarChart3, Gavel, Flag, Users, Sparkles, Bell, Shield, MessageSquareOff, Gamepad2, Megaphone, TrendingDown, Trophy, Zap } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import AdminAnalytics from "./AdminAnalytics";
import ModReportsTab from "./ModReportsTab";
import UsersTab from "./UsersTab";
import AdminByolTab from "./AdminByolTab";
import MissingSubmissionsTab from "./MissingSubmissionsTab";
import NonRespondingTab from "./NonRespondingTab";
import ModeratorsTab from "./ModeratorsTab";
import SendReminderButton from "../components/SendReminderButton";
import ProtectedImage from "../components/ProtectedImage";
import PlayerReportsTab from "./PlayerReportsTab";
import ReliabilityTab from "./ReliabilityTab";
import ClassicsDisputesTab from "./ClassicsDisputesTab";
import ClassicsAnalyticsTab from "./ClassicsAnalyticsTab";
import FriendliesAdminTab from "./FriendliesAdminTab";
import AdminProClubsTab from "./AdminProClubsTab";
import AdminWCTourneyTab from "./AdminWCTourneyTab";
import AdminWc26LeagueTab, { WcVisibilityCard } from "./AdminWc26LeagueTab";
import AdminDtReportsTab from "./AdminDtReportsTab";
import AdminUflTab from "./AdminUflTab";
import AdminWcDisputesTab from "./AdminWcDisputesTab";
import AdminCtfTab from "./AdminCtfTab";
import BroadcastsTab from "./BroadcastsTab";
import RetentionTab from "./RetentionTab";

function DisputesTab() {
  const [disputed, setDisputed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [homeScore, setHomeScore] = useState(0);
  const [awayScore, setAwayScore] = useState(0);

  const refresh = async () => {
    try {
      const { data } = await api.get("/admin/matches/disputed");
      setDisputed(data);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const openMatch = (m) => {
    setSelected(m);
    const h = m.home_submission?.score_home ?? 0;
    const a = m.home_submission?.score_away ?? 0;
    setHomeScore(h); setAwayScore(a);
  };

  const resolve = async () => {
    try {
      await api.post(`/admin/matches/${selected.id}/override`, { home_score: homeScore, away_score: awayScore });
      toast.success("Dispute resolved.");
      setSelected(null);
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-8">
      <SectionTitle kicker="// admin control" testId="admin-title">Dispute Resolution</SectionTitle>
      {loading ? <TactLoader /> : disputed.length === 0 ? (
        <Panel className="font-mono text-[#71717A] text-sm">[ NO DISPUTED MATCHES. ALL CLEAR, COMMANDER. ]</Panel>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-disputed-list">
          {disputed.map((m) => {
            const homeMissing = !m.home_submission;
            const awayMissing = !m.away_submission;
            return (
              <div key={m.id} className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-t-2 border-t-[#E11D48] p-5" data-testid={`admin-match-${m.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">RD {m.round}</span>
                  <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#E11D48]">DISPUTED</span>
                </div>
                <div className="font-heading text-xl uppercase">
                  <span className={homeMissing ? "text-[#E11D48]" : ""}>{m.home?.username}</span>
                  <span className="text-[#71717A] text-sm font-mono mx-2">VS</span>
                  <span className={awayMissing ? "text-[#E11D48]" : ""}>{m.away?.username}</span>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2 font-mono text-sm">
                  <SubmissionCell
                    side="home"
                    submission={m.home_submission}
                    missing={homeMissing}
                    user={m.home}
                    matchId={m.id}
                    onReminderSent={refresh}
                  />
                  <SubmissionCell
                    side="away"
                    submission={m.away_submission}
                    missing={awayMissing}
                    user={m.away}
                    matchId={m.id}
                    onReminderSent={refresh}
                  />
                </div>
                <div className="mt-4 flex gap-2">
                  <button data-testid={`resolve-btn-${m.id}`} onClick={() => openMatch(m)} className="bg-[#7C3AED] text-white font-heading uppercase tracking-widest px-4 py-2 text-xs hover:bg-[#6D28D9] transition-colors">RESOLVE</button>
                  <Link to={`/matches/${m.id}`} className="border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-2 text-xs uppercase tracking-widest hover:border-[#7C3AED] hover:text-[#7C3AED] transition-colors font-body">View</Link>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" data-testid="resolve-modal">
          <div className="bg-[#FFFFFF] dark:bg-[#1A1A1E] border border-[#E4E4E7] dark:border-[#2A2A30] border-t-2 border-t-[#7C3AED] max-w-md w-full p-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A] mb-2">// finalize result</div>
            <h3 className="font-heading text-2xl uppercase mb-6">{selected.home?.username} vs {selected.away?.username}</h3>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">Home Score</label>
                <input data-testid="admin-home-score" type="number" min={0} max={99} value={homeScore} onChange={(e) => setHomeScore(Number(e.target.value))} className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-3 text-[#09090B] dark:text-[#F4F4F5] font-mono text-xl focus:outline-none focus:ring-1 focus:ring-[#7C3AED]" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-[#71717A] font-bold mb-2">Away Score</label>
                <input data-testid="admin-away-score" type="number" min={0} max={99} value={awayScore} onChange={(e) => setAwayScore(Number(e.target.value))} className="w-full bg-[#FAFAFC] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-3 text-[#09090B] dark:text-[#F4F4F5] font-mono text-xl focus:outline-none focus:ring-1 focus:ring-[#7C3AED]" />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button data-testid="admin-cancel-btn" onClick={() => setSelected(null)} className="border border-[#E4E4E7] dark:border-[#2A2A30] px-4 py-2 text-xs uppercase tracking-widest hover:border-[#E11D48] hover:text-[#E11D48] transition-colors font-body">Cancel</button>
              <button data-testid="admin-confirm-btn" onClick={resolve} className="bg-[#7C3AED] text-white font-heading uppercase tracking-widest px-6 py-2 text-xs hover:bg-[#6D28D9] transition-colors">Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * One side of a disputed match — score, screenshot evidence, and a reminder
 * button if the player never submitted. Mods can view; admins can also act.
 */
function SubmissionCell({ side, submission, missing, user, matchId, onReminderSent }) {
  return (
    <div className="border border-[#E4E4E7] dark:border-[#2A2A30] p-2" data-testid={`submission-cell-${side}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] text-[#71717A] uppercase">{side === "home" ? "Home reported" : "Away reported"}</div>
        {missing && user?.id && (
          <SendReminderButton
            matchId={matchId}
            userId={user.id}
            username={user.username}
            currentLevel={0}
            size="sm"
            onSent={onReminderSent}
          />
        )}
      </div>
      <div className={missing ? "text-[#E11D48] uppercase text-[10px] tracking-[0.2em]" : ""}>
        {submission ? `${submission.score_home} : ${submission.score_away}` : "— not submitted"}
      </div>
      {submission?.screenshot_path && (
        <ProtectedImage
          path={submission.screenshot_path}
          alt={`${side} score evidence`}
          testId={`submission-screenshot-${side}`}
        />
      )}
    </div>
  );
}

export default function AdminPanel() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useT();
  const isAdmin = user?.role === "admin";
  const raw = params.get("tab");
  const VALID = isAdmin
    ? ["analytics", "retention", "broadcasts", "classics-analytics", "disputes", "classics-disputes", "reminders", "non-responding", "player-reports", "reliability", "mod-reports", "moderators", "users", "byol", "friendlies", "proclubs", "wc26", "ufl", "dt-reports"]
    : ["disputes", "classics-disputes", "reminders", "non-responding", "player-reports", "ufl", "dt-reports"];
  const tab = VALID.includes(raw) ? raw : (isAdmin ? "analytics" : "disputes");
  const setTab = (t) => setParams({ tab: t }, { replace: true });
  // WC26 sub-tab routing (?wcsub=tournament|immersion|dream-team|ctf|disputes)
  const WC_SUBS = ["tournament", "immersion", "dream-team", "ctf", "disputes"];
  const wcsub = WC_SUBS.includes(params.get("wcsub")) ? params.get("wcsub") : "immersion";
  const setWcsub = (s) => setParams({ tab: "wc26", wcsub: s }, { replace: true });

  return (
    <div data-testid="admin-page" className="space-y-6">
      <div className="flex gap-1 border-b border-[#E4E4E7] dark:border-[#2A2A30] flex-wrap" data-testid="admin-tabs">
        {isAdmin && (
          <button
            data-testid="admin-tab-analytics"
            onClick={() => setTab("analytics")}
            className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
              tab === "analytics"
                ? "border-[#7C3AED] text-[#7C3AED]"
                : "border-transparent text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
            }`}
          >
            <BarChart3 className="w-3.5 h-3.5" /> Overview
          </button>
        )}
        {isAdmin && (
          <button
            data-testid="admin-tab-retention"
            onClick={() => setTab("retention")}
            className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
              tab === "retention"
                ? "border-[#EF4444] text-[#EF4444]"
                : "border-transparent text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
            }`}
          >
            <TrendingDown className="w-3.5 h-3.5" /> DAU Drop
          </button>
        )}
        {isAdmin && (
          <button
            data-testid="admin-tab-broadcasts"
            onClick={() => setTab("broadcasts")}
            className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
              tab === "broadcasts"
                ? "border-[#7C3AED] text-[#7C3AED]"
                : "border-transparent text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
            }`}
          >
            <Megaphone className="w-3.5 h-3.5" /> Notifications
          </button>
        )}
        {isAdmin && (
          <button
            data-testid="admin-tab-classics-analytics"
            onClick={() => setTab("classics-analytics")}
            className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
              tab === "classics-analytics"
                ? "border-[#7C3AED] text-[#7C3AED]"
                : "border-transparent text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
            }`}
          >
            <Gamepad2 className="w-3.5 h-3.5" /> Classics Stats
          </button>
        )}
        <button
          data-testid="admin-tab-disputes"
          onClick={() => setTab("disputes")}
          className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
            tab === "disputes"
              ? "border-[#E11D48] text-[#E11D48]"
              : "border-transparent text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
          }`}
        >
          <Gavel className="w-3.5 h-3.5" /> Disputes
        </button>
        <button
          data-testid="admin-tab-classics-disputes"
          onClick={() => setTab("classics-disputes")}
          className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
            tab === "classics-disputes"
              ? "border-[#E11D48] text-[#E11D48]"
              : "border-transparent text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
          }`}
        >
          <Gamepad2 className="w-3.5 h-3.5" /> Classics
        </button>
        <button
          data-testid="admin-tab-reminders"
          onClick={() => setTab("reminders")}
          className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
            tab === "reminders"
              ? "border-[#EA580C] text-[#EA580C]"
              : "border-transparent text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
          }`}
        >
          <Bell className="w-3.5 h-3.5" /> Reminders
        </button>
        <button
          data-testid="admin-tab-non-responding"
          onClick={() => setTab("non-responding")}
          className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
            tab === "non-responding"
              ? "border-[#00FF87] text-[#00FF87]"
              : "border-transparent text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
          }`}
        >
          <MessageSquareOff className="w-3.5 h-3.5" /> {t("admin.nonResponding")}
        </button>
        {/* Player Reports — visible to mods (read-only) AND admins (with ban/unban) */}
        <button
          data-testid="admin-tab-player-reports"
          onClick={() => setTab("player-reports")}
          className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
            tab === "player-reports"
              ? "border-[#E11D48] text-[#E11D48]"
              : "border-transparent text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
          }`}
        >
          <Flag className="w-3.5 h-3.5" /> Player Reports
        </button>
        {isAdmin && (
          <button
            data-testid="admin-tab-reliability"
            onClick={() => setTab("reliability")}
            className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
              tab === "reliability"
                ? "border-[#00FF87] text-[#00FF87]"
                : "border-transparent text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
            }`}
          >
            <Shield className="w-3.5 h-3.5" /> Reliability
          </button>
        )}
        {isAdmin && (
          <button
            data-testid="admin-tab-mod-reports"
            onClick={() => setTab("mod-reports")}
            className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
              tab === "mod-reports"
                ? "border-[#EA580C] text-[#EA580C]"
                : "border-transparent text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
            }`}
          >
            <Flag className="w-3.5 h-3.5" /> Mod Reports
          </button>
        )}
        {isAdmin && (
          <button
            data-testid="admin-tab-moderators"
            onClick={() => setTab("moderators")}
            className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
              tab === "moderators"
                ? "border-[#7C3AED] text-[#7C3AED]"
                : "border-transparent text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
            }`}
          >
            <Shield className="w-3.5 h-3.5" /> {t("admin.moderators")}
          </button>
        )}
        {isAdmin && (
          <button
            data-testid="admin-tab-users"
            onClick={() => setTab("users")}
            className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
              tab === "users"
                ? "border-[#7C3AED] text-[#7C3AED]"
                : "border-transparent text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
            }`}
          >
            <Users className="w-3.5 h-3.5" /> Users
          </button>
        )}
        {isAdmin && (
          <button
            data-testid="admin-tab-byol"
            onClick={() => setTab("byol")}
            className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
              tab === "byol"
                ? "border-[#7C3AED] text-[#7C3AED]"
                : "border-transparent text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" /> BYOL
          </button>
        )}
        {isAdmin && (
          <button
            data-testid="admin-tab-friendlies"
            onClick={() => setTab("friendlies")}
            className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
              tab === "friendlies"
                ? "border-[#00FF87] text-[#00FF87]"
                : "border-transparent text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
            }`}
          >
            <Gamepad2 className="w-3.5 h-3.5" /> Friendlies
          </button>
        )}
        {isAdmin && (
          <button
            data-testid="admin-tab-proclubs"
            onClick={() => setTab("proclubs")}
            className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
              tab === "proclubs"
                ? "border-[#00FF87] text-[#00FF87]"
                : "border-transparent text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
            }`}
          >
            <Gamepad2 className="w-3.5 h-3.5" /> Pro Clubs
          </button>
        )}
        {isAdmin && (
          <button
            data-testid="admin-tab-wc26"
            onClick={() => setTab("wc26")}
            className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
              tab === "wc26"
                ? "border-[#FFD700] text-[#FFD700]"
                : "border-transparent text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
            }`}
          >
            <Trophy className="w-3.5 h-3.5" /> WC 2026
          </button>
        )}
        <button
          data-testid="admin-tab-ufl"
          onClick={() => setTab("ufl")}
          className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
            tab === "ufl"
              ? "border-[#60A5FA] text-[#60A5FA]"
              : "border-transparent text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
          }`}
        >
          <Zap className="w-3.5 h-3.5" /> UFL
        </button>
        <button
          data-testid="admin-tab-dt-reports"
          onClick={() => setTab("dt-reports")}
          className={`inline-flex items-center gap-2 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors ${
            tab === "dt-reports"
              ? "border-[#EF4444] text-[#EF4444]"
              : "border-transparent text-[#A1A1AA] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"
          }`}
        >
          <Flag className="w-3.5 h-3.5" /> DT Reports
        </button>
      </div>

      {tab === "analytics" && isAdmin && <AdminAnalytics />}
      {tab === "retention" && isAdmin && <RetentionTab />}
      {tab === "broadcasts" && isAdmin && <BroadcastsTab />}
      {tab === "disputes" && <DisputesTab />}
      {tab === "classics-disputes" && <ClassicsDisputesTab />}
      {tab === "classics-analytics" && isAdmin && <ClassicsAnalyticsTab />}
      {tab === "reminders" && <MissingSubmissionsTab />}
      {tab === "non-responding" && <NonRespondingTab />}
      {tab === "player-reports" && <PlayerReportsTab />}
      {tab === "reliability" && isAdmin && <ReliabilityTab />}
      {tab === "mod-reports" && isAdmin && <ModReportsTab />}
      {tab === "moderators" && isAdmin && <ModeratorsTab />}
      {tab === "users" && isAdmin && <UsersTab />}
      {tab === "byol" && isAdmin && <AdminByolTab />}
      {tab === "friendlies" && isAdmin && <FriendliesAdminTab />}
      {tab === "proclubs" && isAdmin && <AdminProClubsTab />}
      {tab === "wc26" && isAdmin && (
        <div className="space-y-4" data-testid="admin-wc26-shell">
          <div className="rounded-2xl border border-[#FFD700]/30 bg-gradient-to-r from-[#FFD700]/10 via-transparent to-[#FFD700]/10 p-4 flex items-center justify-between flex-wrap gap-3" data-testid="admin-wc26-banner">
            <div className="inline-flex items-center gap-2.5">
              <Trophy className="w-5 h-5 text-[#FFD700]" />
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFD700]">// FIFA WORLD CUP · 2026</div>
                <div className="font-heading text-base uppercase tracking-wider">All 3 modes · One control surface</div>
              </div>
            </div>
            <div className="text-[11px] font-mono text-[#71717A]">Kickoff Jun 11, 2026 · 12:00 ET</div>
          </div>
          {/* iter 96 — Mode visibility gates surfaced at the top level so any
              mode (incl. Collect The Flags) and registration can be opened from
              one place, independent of the per-mode dashboards below. */}
          <WcVisibilityCard />
          <div className="flex gap-1 border-b border-[#E4E4E7] dark:border-[#2A2A30] flex-wrap" data-testid="admin-wc26-subtabs">
            {[
              { id: "tournament", label: "Tournament",  color: "#FFD700" },
              { id: "immersion",  label: "Immersion League", color: "#3B82F6" },
              { id: "dream-team", label: "Dream Team",  color: "#10B981" },
              { id: "ctf",        label: "Collect Flags", color: "#60A5FA" },
              { id: "disputes",   label: "Disputes",    color: "#EF4444" },
            ].map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setWcsub(s.id)}
                data-testid={`admin-wc26-sub-${s.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.25em] border-b-2 transition-colors`}
                style={wcsub === s.id ? { borderColor: s.color, color: s.color } : { borderColor: "transparent", color: "#A1A1AA" }}
              >
                {s.label}
              </button>
            ))}
          </div>
          {wcsub === "tournament" && <AdminWCTourneyTab />}
          {wcsub === "immersion"  && <AdminWc26LeagueTab />}
          {wcsub === "dream-team" && <AdminDtReportsTab />}
          {wcsub === "ctf"        && <AdminCtfTab />}
          {wcsub === "disputes"   && <AdminWcDisputesTab />}
        </div>
      )}
      {tab === "dt-reports" && <AdminDtReportsTab />}
      {tab === "ufl" && <AdminUflTab />}
    </div>
  );
}
